/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/solidjs-kobalte
 * @description Single-domain Kobalte-backed adapter detail page.
 */

import type { AdapterDomain } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import { createAdapterStatus } from "@geenius/adapters/solidjs";
import type { JSX } from "solid-js";
import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { AdapterConfigForm, AdapterStatusBadge } from "../components";
import { cn, loadKobalteCore } from "../lib/kobalte";

export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  class?: string;
}

export function AdapterDetailPage(props: AdapterDetailPageProps): JSX.Element {
  const status = createAdapterStatus(props.domain);
  const providers = getProvidersForDomain(props.domain);
  const [core] = createResource(loadKobalteCore);
  const Button = createMemo(() => core()?.Button?.Root);
  const Progress = createMemo(() => core()?.Progress);
  const [isEditing, setIsEditing] = createSignal(false);
  const latency = () => status().latency ?? 0;
  const latencyValue = () => Math.min(100, (latency() / 1000) * 100);

  const backButton = () => (
    <Show
      fallback={
        <button
          class="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-slate-950"
          onClick={props.onBack}
          type="button"
        >
          Back to Adapters
        </button>
      }
      keyed
      when={Button()}
    >
      {(KobalteButton) => (
        <KobalteButton
          class="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-slate-950"
          onClick={props.onBack}
          type="button"
        >
          Back to Adapters
        </KobalteButton>
      )}
    </Show>
  );

  const configureButton = () => (
    <Show
      fallback={
        <button
          class="w-full rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500 transition-colors hover:border-sky-400 hover:text-slate-950"
          onClick={() => setIsEditing(true)}
          type="button"
        >
          Configure {DOMAIN_LABELS[props.domain]} Adapter
        </button>
      }
      keyed
      when={Button()}
    >
      {(KobalteButton) => (
        <KobalteButton
          class="w-full rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500 transition-colors hover:border-sky-400 hover:text-slate-950"
          onClick={() => setIsEditing(true)}
          type="button"
        >
          Configure {DOMAIN_LABELS[props.domain]} Adapter
        </KobalteButton>
      )}
    </Show>
  );

  return (
    <div
      class={cn("mx-auto max-w-3xl space-y-8 text-slate-950", props.class)}
      data-geenius-component="AdapterDetailPage"
      data-geenius-variant="solidjs-kobalte"
    >
      <Show when={props.onBack}>{backButton()}</Show>

      <div class="flex items-start gap-5">
        <div class="flex h-16 w-16 items-center justify-center rounded-lg border border-slate-200 bg-white text-3xl shadow-sm">
          {DOMAIN_ICONS[props.domain]}
        </div>
        <div class="flex-1">
          <div class="flex items-center gap-3">
            <h1 class="text-2xl font-extrabold text-slate-950">
              {DOMAIN_LABELS[props.domain]}
            </h1>
            <AdapterStatusBadge status={status().status} />
          </div>
          <p class="mt-1 text-sm text-slate-500">
            {DOMAIN_DESCRIPTIONS[props.domain]}
          </p>
        </div>
      </div>

      <div class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-xs font-semibold uppercase text-slate-500">
          Current Status
        </h2>
        <div class="grid gap-4 sm:grid-cols-3">
          <div>
            <p class="text-[0.625rem] uppercase text-slate-500">Provider</p>
            <p class="mt-0.5 text-sm font-semibold text-slate-950">
              {status().provider !== "none" ? status().provider : "None"}
            </p>
          </div>
          <div>
            <p class="text-[0.625rem] uppercase text-slate-500">Status</p>
            <p class="mt-0.5">
              <AdapterStatusBadge status={status().status} />
            </p>
          </div>
          <div>
            <p class="text-[0.625rem] uppercase text-slate-500">Last Checked</p>
            <p class="mt-0.5 text-sm text-slate-500">
              <Show when={status().lastCheckedAt} fallback={"Not checked"}>
                {(lastCheckedAt) =>
                  new Date(lastCheckedAt()).toLocaleTimeString()
                }
              </Show>
            </p>
          </div>
        </div>

        <Show when={status().error}>
          <div class="mt-4 rounded-md border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
            <strong>Error:</strong> {status().error}
          </div>
        </Show>

        <Show when={status().latency}>
          <div class="mt-4">
            <p class="mb-1 text-[0.625rem] uppercase text-slate-500">Latency</p>
            <Show
              fallback={
                <div class="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    class={cn(
                      "h-full rounded-full transition-all",
                      latency() < 100
                        ? "bg-emerald-500"
                        : latency() < 500
                          ? "bg-amber-500"
                          : "bg-red-500",
                    )}
                    style={{ width: `${latencyValue()}%` }}
                  />
                </div>
              }
              keyed
              when={Progress()}
            >
              {(KobalteProgress) => (
                <KobalteProgress.Root
                  class="grid gap-1"
                  maxValue={100}
                  value={latencyValue()}
                >
                  <KobalteProgress.Track class="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <KobalteProgress.Fill
                      class={cn(
                        "h-full rounded-full transition-all",
                        latency() < 100
                          ? "bg-emerald-500"
                          : latency() < 500
                            ? "bg-amber-500"
                            : "bg-red-500",
                      )}
                    />
                  </KobalteProgress.Track>
                </KobalteProgress.Root>
              )}
            </Show>
            <p class="mt-1 text-xs text-slate-500">{latency()}ms</p>
          </div>
        </Show>
      </div>

      <div class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xs font-semibold uppercase text-slate-500">
            Available Providers
          </h2>
          <span class="text-xs text-slate-500">
            {providers.length} providers
          </span>
        </div>
        <div class="divide-y divide-slate-200">
          <For each={providers}>
            {(provider) => (
              <div class="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                <div>
                  <p class="text-sm font-semibold text-slate-950">
                    {provider.name}
                  </p>
                  <p class="text-xs text-slate-500">{provider.description}</p>
                </div>
                <span
                  class={cn(
                    "self-start rounded px-2 py-0.5 text-[0.625rem] font-bold uppercase",
                    provider.tier === "pronto"
                      ? "bg-emerald-50 text-emerald-700"
                      : provider.tier === "lancio"
                        ? "bg-sky-50 text-sky-700"
                        : "bg-slate-100 text-slate-600",
                  )}
                >
                  {provider.tier}
                </span>
              </div>
            )}
          </For>
        </div>
      </div>

      <Show when={isEditing()} fallback={configureButton()}>
        <AdapterConfigForm
          domain={props.domain}
          onCancel={() => setIsEditing(false)}
          onSave={async () => {
            setIsEditing(false);
          }}
        />
      </Show>
    </div>
  );
}
